/**
 * Powerbanks page: template MUI components + API.
 * Table with pagination and search by Powerbank ID; row click opens detail drawer.
 * Supports cross-link from Rents (crossLinkPowerbankId in Redux).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import PowerbankDetailDrawer from './PowerbankDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import { setCrossLinkPowerbankId } from '../../modules/powerbanks/powerbanksAction';
import { formatApiDate } from '../utils/formatApiDate';
import {
  canAccessPowerbank,
  getCurrentEmail,
  getCurrentRole,
  isAdmin,
  isSuperAdmin,
  loadAdminMerchantEmails,
} from '../utils/accessControl';

const STATUS_KEYS = {
  RENTED: 'statusRented',
  IN_STATION: 'statusInStation',
  LOST: 'statusLost',
  AT_SUPER_ADMIN: 'statusAtSuperAdmin',
  AT_ADMIN: 'statusAtAdmin',
  AT_MERCHANT: 'statusAtMerchant',
};

const STATUS_COLORS = {
  RENTED: 'success',
  IN_STATION: 'default',
  LOST: 'error',
  AT_SUPER_ADMIN: 'warning',
  AT_ADMIN: 'default',
  AT_MERCHANT: 'warning',
};

function mapPowerbanksToRows(list) {
  if (!Array.isArray(list)) return [];
  return list.map((pb, index) => {
    const station = pb.station || pb.lastStation || null;
    return {
      id: pb.id || pb.powerBankId || `row-${index}`,
      powerBankIdShort: pb.powerBankId ? `...${String(pb.powerBankId).slice(-4)}` : '—',
      powerBankId: pb.powerBankId,
      created: pb.created,
      stationNumber: station?.stationNumber ?? '—',
      stationAddress: station?.address ?? '—',
      slot: pb.slot ?? '—',
      status: pb.status ?? '—',
      checkingStatus: pb.checkingStatus ?? pb.powerBankCheckStatus ?? '—',
      blocked: Boolean(pb.blocked),
      _raw: pb,
    };
  });
}

export default function PowerbanksPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const dispatch = useDispatch();
  const crossLinkPowerbankId = useSelector((s) => s.powerbanksReducer?.crossLinkPowerbankId || '');
  const [rows, setRows] = useState([]);
  const [powerbanksMap, setPowerbanksMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [selectedPowerbank, setSelectedPowerbank] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(null);
  const [merchantEmails, setMerchantEmails] = useState([]);
  const [merchantEmailsReady, setMerchantEmailsReady] = useState(() => !isAdmin(getCurrentRole()));
  const role = getCurrentRole();
  const email = getCurrentEmail();

  useEffect(() => {
    if (!isAdmin(role)) {
      setMerchantEmails([]);
      setMerchantEmailsReady(true);
      return;
    }
    let cancelled = false;
    setMerchantEmailsReady(false);
    loadAdminMerchantEmails(email).then((list) => {
      if (cancelled) return;
      setMerchantEmails(list);
      setMerchantEmailsReady(true);
    });
    return () => { cancelled = true; };
  }, [role, email]);

  const accessOpts = React.useMemo(
    () => ({ role, email, merchantEmails }),
    [role, email, merchantEmails],
  );

  const filterAccessiblePowerbanks = useCallback((list) => {
    if (isSuperAdmin(role)) return Array.isArray(list) ? list : [];
    return (Array.isArray(list) ? list : []).filter((pb) => canAccessPowerbank(pb, accessOpts));
  }, [role, accessOpts]);

  const loadPage = useCallback(async (page, pageSize = 20) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.GetAllPowerbanks(page, pageSize);
      const list = data.powerBankList || [];
      setRows(mapPowerbanksToRows(list));
      const map = {};
      list.forEach((pb) => {
        const key = pb.id || pb.powerBankId;
        if (key) map[key] = pb;
      });
      setPowerbanksMap(map);
      setTotalCount(Number(data.powerBankCount) ?? list.length);
    } catch (e) {
      setError(e?.message || t('powerbanks.errorLoad'));
      setRows([]);
      setPowerbanksMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (crossLinkPowerbankId) return;
    if (searchQuery != null && searchQuery !== '') return;
    loadPage(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, loadPage, crossLinkPowerbankId, searchQuery]);

  // Open powerbank from cross-link (e.g. from Rent detail "Open in Powerbanks")
  useEffect(() => {
    if (!merchantEmailsReady) return;
    const id = (crossLinkPowerbankId || '').toString().trim();
    if (!id) return;
    dispatch(setCrossLinkPowerbankId(''));
    setError(null);
    setLoading(true);
    setSearchQuery(id);
    api
      .FindPowerBank(id)
      .then((res) => {
        const data = res?.data;
        const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
        const accessible = filterAccessiblePowerbanks(list);
        const pb = accessible[0];
        if (list.length > 0 && accessible.length === 0) {
          setError(t('access.powerbankForbidden'));
          setRows([]);
          setPowerbanksMap({});
          setTotalCount(0);
          setSelectedPowerbank(null);
          setDetailOpen(false);
          return;
        }
        if (pb) {
          setRows(mapPowerbanksToRows(accessible));
          const map = {};
          accessible.forEach((p) => {
            const k = p.id || p.powerBankId;
            if (k) map[k] = p;
          });
          setPowerbanksMap(map);
          setTotalCount(accessible.length);
          setSelectedPowerbank(pb);
          setDetailOpen(true);
        } else {
          setRows([]);
          setPowerbanksMap({});
          setTotalCount(0);
        }
      })
      .catch((e) => {
        const status = e?.response?.status;
        setError(
          status === 404 || status === 403
            ? t('access.powerbankForbidden')
            : (e?.message || t('powerbanks.errorNotFound')),
        );
        setRows([]);
        setPowerbanksMap({});
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [crossLinkPowerbankId, dispatch, merchantEmailsReady, filterAccessiblePowerbanks, t]);

  const handleRefresh = useCallback(() => {
    if (!loading) loadPage(paginationModel.page, paginationModel.pageSize);
  }, [loading, loadPage, paginationModel.page, paginationModel.pageSize]);

  const handleSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim();
      if (!q) {
        setSearchQuery(null);
        setSearchInputValue('');
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        loadPage(0, paginationModel.pageSize);
        return;
      }
      setError(null);
      setLoading(true);
      setSearchQuery(q);
      api
        .FindPowerBank(q)
        .then((res) => {
          const data = res?.data;
          const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
          const accessible = filterAccessiblePowerbanks(list);
          if (list.length > 0 && accessible.length === 0) {
            setError(t('access.powerbankForbidden'));
            setRows([]);
            setPowerbanksMap({});
            setTotalCount(0);
            return;
          }
          setRows(mapPowerbanksToRows(accessible));
          const map = {};
          accessible.forEach((p) => {
            const k = p.id || p.powerBankId;
            if (k) map[k] = p;
          });
          setPowerbanksMap(map);
          setTotalCount(accessible.length);
        })
        .catch((e) => {
          const status = e?.response?.status;
          setError(
            status === 404 || status === 403
              ? t('access.powerbankForbidden')
              : (e?.message || t('powerbanks.errorNotFound')),
          );
          setRows([]);
          setPowerbanksMap({});
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    },
    [searchInputValue, loadPage, filterAccessiblePowerbanks, t, paginationModel.pageSize],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchInputValue('');
    setError(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadPage(0, paginationModel.pageSize);
  }, [loadPage, paginationModel.pageSize]);

  const handleRowClick = useCallback(
    (params) => {
      const full = powerbanksMap[params.row.id] || params.row._raw || params.row;
      setSelectedPowerbank(full);
      setDetailOpen(true);
    },
    [powerbanksMap],
  );

  const columns = React.useMemo(
    () => [
      { field: 'powerBankIdShort', headerName: t('powerbanks.columnId'), flex: 0.4, minWidth: 70 },
      {
        field: 'created',
        headerName: t('powerbanks.columnRegistration'),
        flex: 0.8,
        minWidth: 120,
        valueGetter: (value) => formatApiDate(value),
      },
      { field: 'stationNumber', headerName: t('powerbanks.columnStation'), flex: 0.5, minWidth: 90 },
      { field: 'slot', headerName: t('powerbanks.columnSlot'), flex: 0.3, minWidth: 50 },
      {
        field: 'status',
        headerName: t('powerbanks.columnStatus'),
        flex: 0.6,
        minWidth: 100,
        renderCell: (params) => {
          const status = params.value;
          const key = STATUS_KEYS[status];
          const label = key ? t(`powerbanks.${key}`) : status;
          const color = STATUS_COLORS[status] || 'default';
          return <Chip label={label} color={color} size="small" />;
        },
      },
      { field: 'checkingStatus', headerName: t('powerbanks.columnCondition'), flex: 0.5, minWidth: 90 },
      {
        field: 'blocked',
        headerName: t('powerbanks.columnBlocked'),
        flex: 0.4,
        minWidth: 80,
        renderCell: (params) => (
          <Chip
            label={params.value ? t('common.yes') : t('common.no')}
            color={params.value ? 'error' : 'default'}
            size="small"
          />
        ),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('powerbanks.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            {searchQuery != null && (
              <Chip label={t('common.clearSearch')} onDelete={handleClearSearch} size="small" sx={{ mr: 0.5 }} />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={totalCount || rows.length}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: t('common.rowsPerPage'),
                },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('powerbanks.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <PowerbankDetailDrawer
        powerbank={selectedPowerbank}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedPowerbank(null); }}
        onRefresh={handleRefresh}
      />
    </Box>
  );
}
